import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getColors } from "../consts";
import { isEmbeddedPath } from "../../services/embedded";
import { useCrm } from "../../components/CrmUi/useCrm";
import { getHubSpotColors, HUBSPOT_FONT_URL } from "../../components/HubSpotUi/theme";

export type ThemeMode = "light" | "dark";

interface ThemeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = "theme-mode";

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Outreach frames are always light, whatever the dashboard is set to.
  const embedded = typeof window !== "undefined" && isEmbeddedPath(window.location.pathname);
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (embedded) return "light";
    // Check localStorage first
    const savedMode = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null;
    if (savedMode === "light" || savedMode === "dark") {
      return savedMode;
    }
    // Default to light mode
    return "light";
  });
  // HubSpot orgs get HubSpot's palette; Outreach orgs keep the one they had.
  const crm = useCrm();
  // HubSpot has no dark theme, so HubSpot orgs are always light; the saved
  // choice is kept for when the org (or this browser) is on Outreach again.
  const effectiveMode: ThemeMode = crm === "hubspot" ? "light" : mode;

  useEffect(() => {
    // Apply theme class to document root
    const html = document.documentElement;
    html.setAttribute("data-theme", effectiveMode);
    // Also add class for easier CSS targeting
    html.classList.remove("theme-light", "theme-dark");
    html.classList.add(`theme-${effectiveMode}`);
    // Save to localStorage
    if (!embedded) localStorage.setItem(THEME_STORAGE_KEY, mode);
    
    // Single source of truth for the palette lives in consts.ts (HubSpot's
    // in components/HubSpotUi/theme.ts). html[data-crm="hubspot"] scopes the
    // HubSpot stylesheet, so without it none of HubSpot's look applies.
    const colors = crm === "hubspot" ? getHubSpotColors(effectiveMode) : getColors(effectiveMode);
    if (crm === "hubspot") {
      html.setAttribute("data-crm", "hubspot");
      if (!document.getElementById("hubspot-font")) {
        const link = document.createElement("link");
        link.id = "hubspot-font";
        link.rel = "stylesheet";
        link.href = HUBSPOT_FONT_URL;
        document.head.appendChild(link);
      }
    } else {
      html.removeAttribute("data-crm");
    }

    // Set CSS variables
    const root = document.documentElement;
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Guard data-theme from being overwritten by external SDKs
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "data-theme") {
          const current = html.getAttribute("data-theme");
          if (current !== effectiveMode) {
            html.setAttribute("data-theme", effectiveMode);
          }
        }
      }
    });
    observer.observe(html, { attributes: true, attributeFilter: ["data-theme"] });

    return () => observer.disconnect();
  }, [mode, effectiveMode, crm, embedded]);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
  };

  const toggleMode = () => {
    setModeState((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ mode: effectiveMode, toggleMode, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
